'use client';
 
import type { PaletteOptions, TypographyVariantsOptions } from "@mui/material/styles";
import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// const orange1 = '#bc570c';
// const orange2 = '#c57f16';
//
// export const orangeGradient = `linear-gradient(0deg, ${orange1} 0%, ${orange2} 100%)`;

const deepNavy = '#1e2a33';
export const deepNavy2 = '#0b1419';
const deepNavy3 = '#111d23';
const ivoryWhite = '#F9F6F0';
const parchmentBeige = '#ECE0C8';
const antiqueGold = '#C5A45D';
const antiqueGold2 = '#ae8240';
const antiqueGoldDark = '#805f2d'; // Used for bigger contrast in light mode
const antiqueGoldVeryDark = '#3c302b'; // Used for bigger contrast in light mode
const deepCrimson = '#973232';
const warmAmber = '#C67C1E';
const tealBlue = '#1F7A8C';
const forestGreen = '#50855f';

const palette: PaletteOptions = {
  mode: 'dark',
  primary: {
    main: antiqueGold,
    contrastText: deepNavy2,
  },
  secondary: {
    main: antiqueGold2,         // Antique Gold
    contrastText: deepNavy2, // Deep Navy
  },
  background: {
    // default: '#ECE0C8',      // Parchment Beige
    // paper: '#F9F6F0',        // Ivory-white panels
    default: deepNavy2,
    // paper: '#d7be95',
    paper: deepNavy,
  },
  text: {
    primary: ivoryWhite,
    secondary: '#C5A45D',
  },
  error: {
    main: '#c83e3e',         // Deep Crimson but brighter
    contrastText: '#F9F6F0', // Ivory-white
  },
  warning: {
    main: '#C67C1E',         // Warm Amber
    contrastText: deepNavy2,  // Deep Navy
  },
  info: {
    main: '#1F7A8C',         // Teal Blue
    contrastText: '#F9F6F0', // Ivory-white
  },
  success: {
    main: forestGreen,
    contrastText: ivoryWhite,
  },
};

const typography: TypographyVariantsOptions = {
  fontFamily: 'var(--font-merriweather), serif',
  h1: { fontFamily: 'var(--font-cinzel), serif', fontWeight: 700, textWrap: "pretty" },
  h2: { fontFamily: 'var(--font-cinzel), serif', fontWeight: 600, textWrap: "pretty" },
  h3: { textWrap: "pretty" },
  h4: { textWrap: "pretty" },
  h5: { textWrap: "pretty" },
  h6: { textWrap: "pretty" },
  subtitle1: { textWrap: "pretty" },
  subtitle2: { textWrap: "pretty" },
  body1: { textWrap: "pretty" },
  body2: { textWrap: "pretty" },
  caption: { textWrap: "pretty" },
  button: { textWrap: "pretty" },
  overline: { textWrap: "pretty" },
};

export const theme = responsiveFontSizes(createTheme({
  palette,
  typography,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1360, // Changed from 1200 to 1360
      xl: 1536,
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        strong, b {
          font-weight: 600;
        }
      `,
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: deepNavy3,
          backgroundImage: 'none',
          color: ivoryWhite,
          boxShadow: 'none',
          borderBottom: `1px solid ${antiqueGold2}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          // borderRadius: '8px',
          // padding: '8px 16px',
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 10,
        },
        outlined: {
          borderRadius: 10,
          // borderColor: antiqueGold2,
          // borderWidth: 1,
          // backgroundColor: deepNavy3,
          boxShadow: 'rgba(0, 0, 0, 0.4) 0px 2px 8px',
          backgroundColor: "rgba(255, 255, 255, 0.04)", // Deep Navy with transparency
          position: 'relative',
          // Needs to be a pseudo-element to not mess with position fixed.
          '&:before': {
            position: 'absolute',
            backdropFilter: 'blur(2px)',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }
        },
        // elevation1: {
          // backgroundColor: `${deepNavy}55`,
        // }
      },
    },
    // MuiMenu: {
    //   styleOverrides: {
    //     paper: { backgroundColor: deepNavy3 },
    //   },
    // },
    // make every FormControl (and thus Select/TextField, etc.) outlined by default
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        disableInteractive: true,
      }
    },
    MuiFormControl: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    // force every InputLabel to always shrink
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
    },
    // ensure every OutlinedInput renders its notch for the shrunken label
    MuiOutlinedInput: {
      defaultProps: {
        notched: true,
      },
    },
    MuiAlert: {
      defaultProps: {
        variant: 'filled',
      }
    },
    MuiDialog: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 24,
            sx: {
              border: '2px solid',
              borderColor: 'primary.dark',
              backgroundImage: 'none', // disable --Paper-overlay
            },
          },
        }
      }
    }
  },
}));

export const lightPalette: PaletteOptions = {
  primary: {
    main: antiqueGoldDark,
    contrastText: ivoryWhite,
  },
  secondary: {
    main: antiqueGold,
    contrastText: deepNavy2,
  },
  background: {
    default: ivoryWhite,
    paper: parchmentBeige,
  },
  text: {
    primary: antiqueGoldVeryDark,
    secondary: antiqueGoldDark,
  },
  error: {
    main: deepCrimson,
    contrastText: ivoryWhite,
  },
  warning: {
    main: warmAmber,
    contrastText: deepNavy2,
  },
  info: {
    main: tealBlue,
    contrastText: ivoryWhite,
  },
  success: {
    main: forestGreen,
    contrastText: ivoryWhite,
  },
};

export const lightTheme = responsiveFontSizes(createTheme({
  palette: lightPalette,
  typography,
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderColor: antiqueGold,
        },
        rounded: {
          borderRadius: 10,
        },
        outlined: {
          borderRadius: 10,
          // borderColor: antiqueGold2,
          // borderWidth: 1,
          // backgroundColor: deepNavy3,
          boxShadow: 'rgba(0, 0, 0, 0.4) 0px 2px 8px',
        },
      },
    },
    // make every FormControl (and thus Select/TextField, etc.) outlined by default
    MuiFormControl: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    // force every InputLabel to always shrink
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
    },
    // ensure every OutlinedInput renders its notch for the shrunken label
    MuiOutlinedInput: {
      defaultProps: {
        notched: true,
      },
    },
  },
}));

